import { FixedPlacementBox } from './FixedPlacementBox.tsx';
import { PlacementBox } from './PlacementBox.tsx';
import { DropPreviewOutline, Overlay } from './PlacementOverlay.styled.ts';
import { usePlacementGestures } from './usePlacementGestures.ts';
import type {
	CanvasPageInfo,
	CanvasPlacement,
	FixedPlacement,
	PageDimensionsPt,
	PdfBox,
	SlotMeta,
} from './PlacementCanvas.types.ts';

interface Props {
	pageNum: number;
	dims: PageDimensionsPt;
	scale: number;
	placements: { key: string; placement: CanvasPlacement }[];
	/** This page's read-only boxes. */
	fixedPlacements: FixedPlacement[];
	/** Boxes still overlapping another, drawn red. */
	overlappingKeys: Set<string>;
	slotByKey: Map<string, SlotMeta>;
	selectedSlotKey: string | null;
	showMetadata: boolean;
	/** See the note on the field-binding overlay: both are drawn, one is editable. */
	interactive: boolean;
	/** Live rects of every mounted page, so a stamp can be dragged onto another one. */
	collectPages: () => CanvasPageInfo[];
	/** Every box on a page bar `excludeKey`, fixed ones included. */
	obstaclesOnPage: (pageNum: number, excludeKey?: string) => PdfBox[];
	onSelect: (slotKey: string) => void;
	onPlacementChange: (slotKey: string, pageNum: number, pdfBox: PdfBox) => void;
	onRemove?: (slotKey: string) => void;
}

/** The placed stamp boxes for one page. */
export const PlacementOverlay = ({
	pageNum,
	dims,
	scale,
	placements,
	fixedPlacements,
	overlappingKeys,
	slotByKey,
	selectedSlotKey,
	showMetadata,
	interactive,
	collectPages,
	obstaclesOnPage,
	onSelect,
	onPlacementChange,
	onRemove,
}: Props) => {
	const gestures = usePlacementGestures({
		pageNum,
		dims,
		scale,
		collectPages,
		obstaclesOnPage,
		onPlacementChange,
	});

	return (
		<Overlay
			$width={dims.widthPt * scale}
			$height={dims.heightPt * scale}
			$inert={!interactive}
			data-testid={`placement-overlay-${pageNum}`}
		>
			{fixedPlacements.map((fixed) => (
				<FixedPlacementBox
					key={fixed.key}
					fixed={fixed}
					dims={dims}
					scale={scale}
					showMetadata={showMetadata}
				/>
			))}
			{placements.map(({ key, placement }) => (
				<PlacementBox
					key={key}
					slotKey={key}
					placement={placement}
					meta={slotByKey.get(key)}
					dims={dims}
					scale={scale}
					showMetadata={showMetadata}
					isSelected={selectedSlotKey === key}
					isOverlapping={overlappingKeys.has(key)}
					isDropBlocked={gestures.dropPreview?.key === key}
					gestures={gestures.handlers}
					onSelect={onSelect}
					onRemove={onRemove}
				/>
			))}
			{gestures.dropPreview?.landing && (
				<DropPreviewOutline
					style={gestures.dropPreview.landing}
					data-testid="placement-drop-preview"
				/>
			)}
		</Overlay>
	);
};
